// AWS Inventory - tab "Topology". Diagram arsitektur yang digambar otomatis dari
// snapshot inventory (byService milik AWSInventory.jsx), tanpa panggilan API tambahan:
//   Customer Gateway -> VPN Connection -> VGW/TGW -> VPC -> Subnet (urut AZ) -> EC2 (+EBS, EIP, SG)
// Teks UI literal bahasa Inggris (tidak lewat i18n). Ekspor: SVG, PNG, draw.io.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ZoomIn, ZoomOut, Maximize2, Minimize2, RefreshCw, Download, Search, X, FileText, Image as ImageIcon } from 'lucide-react';

const K = {
  CARD_W: 228, CARD_H: 66, CARD_GAP: 10, SUB_PAD: 12, SUB_HEAD: 56, SUB_GAP: 16,
  VPC_HEAD: 60, VPC_PAD: 16, VPC_GAP: 30, PAD: 24, CGW_W: 220, GW_W: 164, GW_H: 72,
  MAX_ROWS: 7, TILE_W: 172, TILE_H: 58, TILE_GAP: 12,
};
const HEALTH = { up: '#10b981', partial: '#f59e0b', down: '#ef4444', unknown: '#94a3b8' };
const STATE_COLOR = { running: '#10b981', stopped: '#94a3b8', stopping: '#f59e0b', pending: '#3b82f6', terminated: '#ef4444' };
// Label eksposur subnet (dari route table): [teks, bg, warna teks]
const EXPO = {
  public: ['PUBLIC', '#ecfdf5', '#047857'],
  'private-nat': ['PRIVATE · NAT', '#f1f5f9', '#475569'],
  'private-nat-instance': ['PRIVATE · NAT EC2', '#f1f5f9', '#475569'],
  'private-other': ['PRIVATE', '#f1f5f9', '#475569'],
  private: ['ISOLATED', '#f8fafc', '#64748b'],
};
const BADGE = { ok: ['#ecfdf5', '#047857'], bad: ['#fef2f2', '#b91c1c'], warn: ['#fffbeb', '#b45309'], none: ['#f1f5f9', '#94a3b8'] };
const CHIP = { lb: ['#eff6ff', '#93c5fd', '#1d4ed8'], ep: ['#f5f3ff', '#c4b5fd', '#6d28d9'], peer: ['#fdf2f8', '#f9a8d4', '#be185d'], cert: ['#f0fdf4', '#86efac', '#15803d'] };
const FONT = "Inter, 'Segoe UI', system-ui, -apple-system, sans-serif";

const trunc = (s, n) => { s = String(s == null ? '' : s); return s.length > n ? s.slice(0, n - 1) + '…' : s; };
const nm = (r) => (r && (r.name || r.id)) || '';
const regOf = (x) => x.region || x._region || 'global';
const plural = (n, w) => n + ' ' + w + (n === 1 ? '' : 's');

function vpnHealth(v) {
  const t = v.tunnels || [];
  const up = t.filter((x) => String(x.status || '').toUpperCase() === 'UP').length;
  if (!t.length) return 'unknown';
  if (up === t.length) return 'up';
  return up > 0 ? 'partial' : 'down';
}

const REGIONAL_TILES = [
  ['security_group', 'Security Groups'], ['ebs_snapshots', 'EBS Snapshots'], ['ami', 'AMIs'],
  ['key_pairs', 'Key Pairs'], ['lambda', 'Lambda Functions'], ['rds', 'RDS'],
  ['cloudwatch_alarm', 'CloudWatch Alarms'], ['glacier_vault', 'Glacier Vaults'],
  ['aws_config', 'AWS Config'], ['security_hub', 'Security Hub findings'],
  ['ec2_reserved', 'EC2 Reserved Instances'], ['rds_reserved', 'RDS Reserved Instances'],
  ['route_table', 'Route Tables'], ['network_interface', 'Network Interfaces'],
  ['ssm_instance', 'SSM Managed Instances'], ['log_group', 'CloudWatch Log Groups'],
  ['opensearch', 'OpenSearch Domains'], ['acm_certificate', 'ACM Certificates'],
];
const GLOBAL_TILES = [['iam_users', 'IAM Users'], ['iam_roles', 'IAM Roles'], ['s3', 'S3 Buckets'], ['savings_plans', 'Savings Plans'], ['budgets', 'Budgets']];

export function topologyRegions(bs) {
  const score = {};
  ['vpc', 'subnet', 'ec2_instances', 'vpn_connection', 'vpn_gateway', 'customer_gateway', 'ebs_volumes', 'eip'].forEach((k) => {
    (bs[k] || []).forEach((x) => {
      const r = regOf(x); if (r === 'global') return;
      score[r] = (score[r] || 0) + (k === 'ec2_instances' ? 10 : 1);
    });
  });
  return Object.keys(score).sort((a, b) => score[b] - score[a]);
}

export function buildTopology(bs, region, opts) {
  const all = (k) => bs[k] || [];
  const inR = (k) => all(k).filter((x) => regOf(x) === region);
  const vpcs = inR('vpc');
  const subnets = inR('subnet');
  const ec2All = inR('ec2_instances');
  const ec2 = opts.showStopped ? ec2All : ec2All.filter((i) => i.state !== 'stopped');
  const vols = {}; all('ebs_volumes').forEach((v) => { vols[v.id] = v; });
  const eipByInst = {}; inR('eip').forEach((e) => { if (e.instanceId) eipByInst[e.instanceId] = e; });
  const vgws = inR('vpn_gateway');
  const vpns = inR('vpn_connection');
  const cgwsRaw = inR('customer_gateway');
  const rts = inR('route_table');
  const igwByVpc = {}; inR('internet_gateway').forEach((g) => (g.attachments || []).forEach((a) => { if (a.vpcId) igwByVpc[a.vpcId] = g; }));
  const natBySub = {}; inR('nat_gateway').forEach((n) => { (natBySub[n.subnetId] = natBySub[n.subnetId] || []).push({ ...n, _nat: true }); });
  const osBySub = {}; inR('opensearch').forEach((d) => { const sid = (d.subnetIds || [])[0]; if (sid) (osBySub[sid] = osBySub[sid] || []).push({ ...d, _os: true }); });
  const certs = inR('acm_certificate');
  const rtBySub = {}; const mainRt = {};
  rts.forEach((r) => { (r.subnetIds || []).forEach((s) => { rtBySub[s] = r; }); if (r.main) mainRt[r.vpcId] = r; });
  const exposureOf = (s) => { const r = rtBySub[s.id] || mainRt[s.vpcId]; return r ? r.exposure : null; };
  const lbs = inR('load_balancer'); const eps = inR('vpc_endpoint'); const peers = inR('vpc_peering');
  const chips = []; const igws = []; const stripLabels = [];
  const ssmById = {}; inR('ssm_instance').forEach((s) => { ssmById[s.id] = s; });

  const rects = []; const cards = []; const edges = []; const gws = []; const cgws = []; const tiles = []; const sections = [];

  // --- EC2 per subnet ---
  const subById = {}; subnets.forEach((s) => { subById[s.id] = s; });
  const instBySub = {}; const noSub = {};
  ec2.forEach((i) => {
    if (i.subnetId && subById[i.subnetId]) (instBySub[i.subnetId] = instBySub[i.subnetId] || []).push(i);
    else { const k = i.vpcId || '__none__'; (noSub[k] = noSub[k] || []).push(i); }
  });
  const vpcList = vpcs.slice();
  Object.keys(noSub).forEach((k) => {
    if (!vpcList.find((v) => v.id === k)) vpcList.push({ id: k, name: k === '__none__' ? 'Outside any VPC' : k, cidr: '', _synthetic: true });
  });
  const countIn = (v) => ec2.filter((i) => (i.vpcId || '__none__') === v.id).length;
  vpcList.sort((a, b) => (countIn(b) - countIn(a)) || ((a.isDefault ? 1 : 0) - (b.isDefault ? 1 : 0)) || nm(a).localeCompare(nm(b)));

  // --- gateway (VGW + Transit GW semu dari VPN) & customer gateway ---
  const gwList = vgws.map((g) => ({ ...g, _kind: 'vgw' })).concat(inR('transit_gateway').map((g) => ({ ...g, _kind: 'tgw' })));
  vpns.forEach((v) => {
    if (!v.vpnGatewayId && v.transitGatewayId && !gwList.find((g) => g.id === v.transitGatewayId))
      gwList.push({ id: v.transitGatewayId, name: v.transitGatewayId, _kind: 'tgw', _synthetic: true });
  });
  const cgwList = cgwsRaw.slice();
  vpns.forEach((v) => {
    if (v.customerGatewayId && !cgwList.find((c) => c.id === v.customerGatewayId))
      cgwList.push({ id: v.customerGatewayId, name: v.customerGatewayId, _synthetic: true });
  });

  const hasLeft = gwList.length > 0 || cgwList.length > 0;
  const cgwX = K.PAD + 16;
  const gwX = cgwX + K.CGW_W + 96;
  const vpcX = hasLeft ? gwX + K.GW_W + 76 : K.PAD + 22;
  const top = K.PAD + 100;

  // --- VPC -> subnet -> EC2 ---
  let y = top; let maxRight = vpcX + 340; const vpcPos = {};
  vpcList.forEach((v) => {
    const subs = subnets.filter((s) => s.vpcId === v.id)
      .sort((a, b) => String(a.az || '').localeCompare(String(b.az || '')) || nm(a).localeCompare(nm(b)));
    const groups = subs.map((s) => ({ sub: s, items: instBySub[s.id] || [], nats: (natBySub[s.id] || []).concat(osBySub[s.id] || []) }));
    if (noSub[v.id] && noSub[v.id].length)
      groups.push({ sub: { id: v.id + ':nosub', name: 'Subnet not in inventory', cidr: '', az: '' }, items: noSub[v.id], synthetic: true });
    let x = vpcX + K.VPC_PAD; let innerH = 0;
    const placed = groups.map((g) => {
      const items = (g.nats || []).concat(g.items.slice().sort((a, b) =>
        ((a.state === 'running' ? 0 : 1) - (b.state === 'running' ? 0 : 1)) || nm(a).localeCompare(nm(b))));
      const n = items.length;
      const cols = Math.max(1, Math.min(3, Math.ceil(n / K.MAX_ROWS)));
      const rows = n ? Math.ceil(n / cols) : 0;
      const w = cols * K.CARD_W + (cols - 1) * K.CARD_GAP + K.SUB_PAD * 2;
      const h = K.SUB_HEAD + (rows ? rows * (K.CARD_H + K.CARD_GAP) - K.CARD_GAP + K.SUB_PAD : 30);
      const p = { g, items, rows, x, w, h };
      x += w + K.SUB_GAP; innerH = Math.max(innerH, h);
      return p;
    });
    const w = Math.max(420, groups.length ? x - K.SUB_GAP + K.VPC_PAD - vpcX : 420);
    const vy = y; const nInst = countIn(v);
    // Strip di dasar VPC: load balancer, VPC endpoint, peering.
    const vChips = [];
    lbs.filter((l) => l.vpcId === v.id).forEach((l) => vChips.push({
      id: l.id, svc: 'load_balancer', row: l, kind: 'lb', bad: l.targetCount === 0 || l.healthyCount < l.targetCount,
      label: (l.type === 'network' ? 'NLB ' : l.type === 'gateway' ? 'GWLB ' : 'ALB ') + trunc(l.name, 26) + ' · ' + l.healthyCount + '/' + l.targetCount + ' healthy',
      tip: [l.name, (l.type || '') + ' · ' + (l.scheme || ''), l.dnsName, 'Listeners: ' + (l.listeners || '-'), l.healthyCount + ' of ' + l.targetCount + ' targets healthy'].join('\n'),
      search: [l.id, l.name, l.dnsName].join(' ').toLowerCase(),
    }));
    const lbArns = lbs.filter((l) => l.vpcId === v.id).map((l) => l.arn);
    certs.filter((c) => (c.inUseArns || []).some((a) => lbArns.indexOf(a) >= 0)).forEach((c) => vChips.push({
      id: c.id, svc: 'acm_certificate', row: c, kind: 'cert', bad: c.status !== 'ISSUED' || (c.daysLeft != null && c.daysLeft <= 30),
      label: 'Cert · ' + trunc(c.domain, 28) + (c.daysLeft != null ? ' · ' + c.daysLeft + 'd' : ''),
      tip: [c.domain, 'Status: ' + c.status + ' (' + (c.type || '') + ')', 'Expires: ' + (c.notAfter || '-'), 'Used by: ' + (c.inUseBy || '-')].join('\n'),
      search: [c.id, c.domain, c.sans].join(' ').toLowerCase(),
    }));
    eps.filter((e) => e.vpcId === v.id).forEach((e) => vChips.push({
      id: e.id, svc: 'vpc_endpoint', row: e, kind: 'ep', label: 'Endpoint · ' + e.service + (e.type ? ' (' + e.type + ')' : ''),
      tip: [e.serviceName, e.id, e.type, e.state].filter(Boolean).join('\n'), search: [e.id, e.serviceName].join(' ').toLowerCase(),
    }));
    peers.filter((pp) => pp.requesterVpc === v.id || pp.accepterVpc === v.id).forEach((pp) => {
      const o = (pp.requesterVpc === v.id ? pp.accepter : pp.requester) || {};
      vChips.push({
        id: pp.id, svc: 'vpc_peering', row: pp, kind: 'peer', bad: pp.status !== 'active',
        label: 'Peering ↔ ' + (o.cidr || o.vpcId || '?') + (o.region && o.region !== region ? ' (' + o.region + ')' : ''),
        tip: [nm(pp) + ' (' + pp.id + ')', 'Status: ' + pp.status, 'Peer VPC: ' + (o.vpcId || '-') + ' ' + (o.cidr || ''),
          'Peer account: ' + (o.ownerId || '-'), 'Peer region: ' + (o.region || '-')].join('\n'),
        search: [pp.id, pp.name, o.vpcId, o.cidr].join(' ').toLowerCase(),
      });
    });
    let h = K.VPC_HEAD + (groups.length ? innerH + K.VPC_PAD : 44);
    if (vChips.length) {
      const y0 = vy + K.VPC_HEAD + (groups.length ? innerH + 12 : 6);
      stripLabels.push({ id: v.id, x: vpcX + K.VPC_PAD, y: y0 + 10, label: 'LOAD BALANCERS · CERTIFICATES · ENDPOINTS · PEERING' });
      let cx = vpcX + K.VPC_PAD; let cyy = y0 + 18;
      vChips.forEach((ch) => {
        const cw = Math.min(340, 30 + ch.label.length * 6.1);
        if (cx + cw > vpcX + w - K.VPC_PAD && cx > vpcX + K.VPC_PAD) { cx = vpcX + K.VPC_PAD; cyy += 32; }
        chips.push({ ...ch, x: cx, y: cyy, w: cw, h: 24 });
        cx += cw + 8;
      });
      h = cyy + 24 + K.VPC_PAD - vy;
    }
    const ig = igwByVpc[v.id];
    if (ig) igws.push({ id: ig.id, svc: 'internet_gateway', row: ig, x: vpcX + w - 200, y: vy - 11, w: 184, h: 22, label: 'IGW · ' + trunc(nm(ig), 20) });
    rects.push({
      id: v.id, kind: 'vpc', svc: v._synthetic ? null : 'vpc', row: v, x: vpcX, y: vy, w, h,
      title: nm(v) + (v.isDefault ? ' (default)' : ''),
      sub: [v.cidr, v._synthetic ? '' : v.id].filter(Boolean).join('  ·  '),
      stat: plural(nInst, 'instance') + ' · ' + plural(subs.length, 'subnet'),
      tip: [nm(v), v.id, v.cidr, v.isDefault ? 'Default VPC' : ''].filter(Boolean).join('\n'),
      empty: !groups.length && !vChips.length,
    });
    placed.forEach((p) => {
      const sy = vy + K.VPC_HEAD; const s = p.g.sub;
      rects.push({
        id: s.id, kind: 'subnet', svc: p.g.synthetic ? null : 'subnet', row: s, x: p.x, y: sy, w: p.w, h: p.h,
        title: nm(s), sub: [s.cidr, s.az].filter(Boolean).join('  ·  '), synthetic: p.g.synthetic, empty: !p.items.length,
        exposure: p.g.synthetic ? null : exposureOf(s),
        tip: [nm(s), s.id, s.cidr, s.az, s.availableIps != null ? s.availableIps + ' free IPs' : ''].filter(Boolean).join('\n'),
      });
      p.items.forEach((i, idx) => {
        const c = Math.floor(idx / p.rows); const r = idx % p.rows;
        if (i._os) {
          cards.push({
            id: i.id, svc: 'opensearch', row: i, kind: 'os',
            x: p.x + K.SUB_PAD + c * (K.CARD_W + K.CARD_GAP), y: sy + K.SUB_HEAD + r * (K.CARD_H + K.CARD_GAP), w: K.CARD_W, h: K.CARD_H,
            state: i.state === 'active' ? 'running' : 'pending', title: 'OpenSearch · ' + nm(i),
            line2: i.instanceCount + ' × ' + (i.instanceType || '?'),
            ebs: (i.engineVersion || '') + (i.storageGiB ? ' · ' + i.storageGiB + ' GiB' : '') + ' · ' + i.state,
            pub: '', eip: false, sgs: [], search: [i.id, i.name, i.endpoint, 'opensearch'].join(' ').toLowerCase(),
            tip: [nm(i) + ' (OpenSearch)', 'Engine: ' + (i.engineVersion || '-'), 'Nodes: ' + i.instanceCount + ' × ' + (i.instanceType || '-'),
              'Storage: ' + (i.storageGiB != null ? i.storageGiB + ' GiB' : '-'), 'State: ' + i.state, 'Endpoint: ' + (i.endpoint || '-'),
              'Subnets: ' + (i.subnetIds || []).join(', '), 'Security groups: ' + (i.securityGroups || '-')].join('\n'),
          });
          return;
        }
        if (i._nat) {
          cards.push({
            id: i.id, svc: 'nat_gateway', row: i, kind: 'nat',
            x: p.x + K.SUB_PAD + c * (K.CARD_W + K.CARD_GAP), y: sy + K.SUB_HEAD + r * (K.CARD_H + K.CARD_GAP), w: K.CARD_W, h: K.CARD_H,
            state: i.state === 'available' ? 'running' : i.state, title: 'NAT · ' + nm(i),
            line2: [i.publicIp, i.privateIp].filter(Boolean).join('  ·  '), ebs: (i.connectivityType || 'public') + ' NAT gateway · ' + i.state,
            pub: '', eip: false, sgs: [], search: [i.id, i.name, i.publicIp, i.privateIp, 'nat'].join(' ').toLowerCase(),
            tip: [nm(i) + ' (' + i.id + ')', 'State: ' + i.state, 'Public IP: ' + (i.publicIp || '-'), 'Private IP: ' + (i.privateIp || '-'), 'Subnet: ' + i.subnetId].join('\n'),
          });
          return;
        }
        const bd = i.blockDevices || [];
        const size = bd.reduce((t, b) => t + ((vols[b.volumeId] && vols[b.volumeId].sizeGiB) || 0), 0);
        const eip = eipByInst[i.id];
        const pub = (eip && eip.publicIp) || i.publicIp || '';
        const sgs = (i.securityGroups || []).map((g) => g.name || g.id);
        cards.push({
          id: i.id, svc: 'ec2_instances', row: i,
          ...(() => {
            const sI = ssmById[i.id];
            const miss = sI && sI.patchScanned ? (sI.patchMissing || 0) : 0;
            return {
              badge: sI ? (sI.pingStatus === 'Online' ? (miss > 0 ? 'SSM · ' + miss + ' patch' : 'SSM')
                : 'SSM ' + (sI.pingStatus === 'ConnectionLost' ? 'lost' : String(sI.pingStatus || '?').toLowerCase())) : 'no SSM',
              badgeTone: sI ? (sI.pingStatus !== 'Online' ? 'warn' : miss > 0 ? 'bad' : 'ok') : 'none',
              ssmTip: sI ? '\nSSM: ' + sI.pingStatus + (sI.agentVersion ? ' (agent ' + sI.agentVersion + ')' : '')
                + (sI.patchScanned ? '\nPatches: ' + (sI.patchMissing || 0) + ' missing, ' + (sI.patchCriticalMissing || 0) + ' critical, ' + (sI.patchFailed || 0) + ' failed' : '\nPatches: never scanned')
                : '\nSSM: not managed by Systems Manager',
            };
          })(),
          x: p.x + K.SUB_PAD + c * (K.CARD_W + K.CARD_GAP), y: sy + K.SUB_HEAD + r * (K.CARD_H + K.CARD_GAP), w: K.CARD_W, h: K.CARD_H,
          state: i.state, title: nm(i), line2: [i.type, i.privateIp].filter(Boolean).join('  ·  '),
          ebs: bd.length ? bd.length + ' EBS · ' + size + ' GiB' : 'no EBS', pub, eip: !!eip, sgs,
          search: [i.id, i.name, i.privateIp, i.publicIp, pub, i.type].concat(sgs).join(' ').toLowerCase(),
          tip: [nm(i) + ' (' + i.id + ')', 'State: ' + i.state, 'Type: ' + (i.type || '-'), 'AZ: ' + (i.az || '-'),
            'Private IP: ' + (i.privateIp || '-'), 'Public IP: ' + (pub || '-') + (eip ? ' (Elastic IP)' : ''),
            'Security groups: ' + (sgs.join(', ') || '-'),
            'Volumes: ' + (bd.map((b) => b.volumeId + (vols[b.volumeId] ? ' ' + vols[b.volumeId].sizeGiB + ' GiB' : '')).join(', ') || '-'),
            'Key pair: ' + (i.keyName || '-'), 'Platform: ' + (i.platform || '-')].join('\n'),
        });
      });
    });
    vpcPos[v.id] = { x: vpcX, y: vy, w, h };
    maxRight = Math.max(maxRight, vpcX + w);
    y += h + K.VPC_GAP;
  });
  const vpcBottom = vpcList.length ? y - K.VPC_GAP : top;

  // --- gateway nodes, disejajarkan dengan VPC yang di-attach ---
  // VGW: attachments [{vpcId,state}]; TGW: attachments [{resourceType,resourceId,state}] — bisa banyak VPC.
  const attachList = (g) => (g._kind === 'tgw'
    ? (g.attachments || []).filter((x) => x.resourceType === 'vpc' && x.state !== 'deleted').map((x) => x.resourceId)
    : (g.attachments || []).filter((x) => x.state === 'attached').map((x) => x.vpcId));
  const attachOf = (g) => attachList(g).find((id) => vpcPos[id]) || attachList(g)[0] || null;
  gwList.sort((a, b) => {
    const pa = vpcPos[attachOf(a)]; const pb = vpcPos[attachOf(b)];
    return (pa ? pa.y : 1e9) - (pb ? pb.y : 1e9);
  });
  let gyNext = top;
  gwList.forEach((g) => {
    const vid = attachOf(g); const vp = vpcPos[vid];
    const gy = Math.max(vp ? vp.y + 6 : gyNext, gyNext);
    const vids = attachList(g);
    gws.push({
      id: g.id, kind: g._kind, svc: g._synthetic ? null : (g._kind === 'tgw' ? 'transit_gateway' : 'vpn_gateway'), row: g,
      x: gwX, y: gy, w: K.GW_W, h: K.GW_H,
      title: nm(g), sub: g._kind === 'tgw' ? 'TRANSIT GATEWAY' : 'VPN GATEWAY',
      stat: g.amazonSideAsn ? 'ASN ' + g.amazonSideAsn : (vid ? '' : 'not attached'),
      tip: [nm(g), g.id, g.state ? 'State: ' + g.state : '', vids.length ? 'Attached to ' + vids.join(', ') : 'Not attached to a VPC'].filter(Boolean).join('\n'),
      search: [g.id, g.name].join(' ').toLowerCase(),
    });
    gyNext = gy + K.GW_H + 18;
    vids.filter((id) => vpcPos[id]).forEach((id) => {
      const vq = vpcPos[id];
      const y1 = gy + K.GW_H / 2;
      const y2 = Math.min(Math.max(y1, vq.y + 16), vq.y + vq.h - 16);
      const x1 = gwX + K.GW_W;
      edges.push({
        id: g.id + '>' + id, from: g.id, to: id, kind: 'attach', color: g._kind === 'tgw' ? '#a78bfa' : '#818cf8', width: 2.5,
        d: 'M' + x1 + ' ' + y1 + ' C' + (x1 + 45) + ' ' + y1 + ' ' + (vpcX - 45) + ' ' + y2 + ' ' + vpcX + ' ' + y2,
        tip: nm(g) + ' attached to ' + id,
      });
    });
  });
  const gwBottom = gwList.length ? gyNext - 18 : top;

  // --- customer gateway + garis VPN ---
  const gwIdx = {}; gws.forEach((g, i) => { gwIdx[g.id] = i; });
  const gwOfVpn = (v) => v.vpnGatewayId || v.transitGatewayId;
  const vpnsOf = (c) => vpns.filter((v) => v.customerGatewayId === c.id)
    .sort((a, b) => nm(a).localeCompare(nm(b)));
  const firstGw = (c) => Math.min.apply(null, vpnsOf(c).map((v) => (gwIdx[gwOfVpn(v)] ?? 98)).concat([99]));
  cgwList.sort((a, b) => (firstGw(a) - firstGw(b)) || nm(a).localeCompare(nm(b)));
  const perGw = {};
  cgwList.forEach((c) => vpnsOf(c).forEach((v) => { const g = gwOfVpn(v); (perGw[g] = perGw[g] || []).push(v.id); }));
  let cy = top;
  cgwList.forEach((c) => {
    const vs = vpnsOf(c);
    const h = 54 + vs.length * 22 + (vs.length ? 6 : 0);
    cgws.push({
      id: c.id, kind: 'cgw', svc: c._synthetic ? null : 'customer_gateway', row: c, x: cgwX, y: cy, w: K.CGW_W, h,
      title: nm(c), sub: [c.ipAddress, c.bgpAsn ? 'ASN ' + c.bgpAsn : '', vs.length ? '' : 'unused'].filter(Boolean).join('  ·  '),
      unused: !vs.length,
      vpns: vs.map((v, k) => ({
        id: v.id, row: v, name: nm(v), health: vpnHealth(v), y: cy + 54 + k * 22,
        tunnels: (v.tunnels || []).map((t) => String(t.status || '').toUpperCase()),
        tip: nm(v) + ' (' + v.id + ')\n' + ((v.tunnels || []).map((t) => 'Tunnel ' + (t.outsideIp || '?') + ': ' + t.status).join('\n') || 'No tunnel data'),
      })),
      tip: [nm(c), c.id, c.ipAddress ? 'Public IP: ' + c.ipAddress : '', c.bgpAsn ? 'BGP ASN: ' + c.bgpAsn : '', plural(vs.length, 'VPN connection')].filter(Boolean).join('\n'),
      search: [c.id, c.name, c.ipAddress].concat(vs.map(nm)).join(' ').toLowerCase(),
    });
    vs.forEach((v, k) => {
      const g = gws.find((n) => n.id === gwOfVpn(v));
      if (!g) return;
      const list = perGw[g.id] || []; const pos = list.indexOf(v.id);
      const spread = list.length > 1 ? (pos / (list.length - 1) - 0.5) * (K.GW_H - 16) : 0;
      const x1 = cgwX + K.CGW_W; const y1 = cy + 54 + k * 22 + 7.5;
      const x2 = gwX; const y2 = g.y + K.GW_H / 2 + spread;
      const hl = vpnHealth(v);
      edges.push({
        id: v.id, from: c.id, to: g.id, kind: 'vpn', color: HEALTH[hl], width: 2, dash: hl === 'down' ? '6 4' : null, row: v,
        d: 'M' + x1 + ' ' + y1 + ' C' + (x1 + 70) + ' ' + y1 + ' ' + (x2 - 70) + ' ' + y2 + ' ' + x2 + ' ' + y2,
        tip: nm(v) + ' — ' + ((v.tunnels || []).map((t) => (t.outsideIp || '?') + ' ' + t.status).join(', ') || 'no tunnel data'),
        search: [v.id, v.name, c.id, c.name].join(' ').toLowerCase(),
      });
    });
    cy += h + 12;
  });
  const cgwBottom = cgwList.length ? cy - 12 : top;

  // --- tile ringkasan di bawah diagram ---
  const W0 = Math.max(maxRight + 24, 1000);
  // Bingkai ala AWS architecture diagram: On-premises (customer gateway) & AWS Cloud (region).
const frameTop = top - 46;
const frames = [];
if (cgwList.length) frames.push({ kind: 'onprem', x: cgwX - 16, y: frameTop, w: K.CGW_W + 32, h: cgwBottom + 18 - frameTop, label: 'On-premises', sub: 'Customer gateways' });
if (hasLeft || vpcList.length) {
  const fx = (gwList.length ? gwX : vpcX) - 22;
  frames.push({ kind: 'region', x: fx, y: frameTop, w: maxRight + 22 - fx, h: Math.max(vpcBottom, gwBottom) + 20 - frameTop, label: 'AWS Cloud · ' + region, sub: (gwList.length ? 'VPN gateway → ' : '') + 'VPC → subnet (by AZ) → EC2' });
}
const DH = Math.max(vpcBottom, gwBottom, cgwBottom) + 44;
let ty = DH + 30;
  const addSection = (label, list) => {
    if (!list.length) return;
    sections.push({ label, x: K.PAD, y: ty });
    ty += 12;
    let tx = K.PAD;
    list.forEach((t) => {
      if (tx + K.TILE_W > W0) { tx = K.PAD; ty += K.TILE_H + K.TILE_GAP; }
      tiles.push({ ...t, sec: label, x: tx, y: ty, w: K.TILE_W, h: K.TILE_H });
      tx += K.TILE_W + K.TILE_GAP;
    });
    ty += K.TILE_H + 34;
  };
  const attn = [];
  const unattached = inR('ebs_volumes').filter((v) => v.state === 'available');
  if (unattached.length) attn.push({ key: 'ebs_volumes', label: 'Unattached EBS', count: unattached.length, note: unattached.reduce((t, v) => t + (v.sizeGiB || 0), 0) + ' GiB idle', warn: true });
  const freeEip = inR('eip').filter((e) => !e.instanceId && !e.associationId);
  if (freeEip.length) attn.push({ key: 'eip', label: 'Unassociated EIP', count: freeEip.length, note: 'billed while idle', warn: true });
  const stopped = ec2All.filter((i) => i.state === 'stopped');
  if (stopped.length) attn.push({ key: 'ec2_instances', label: 'Stopped EC2', count: stopped.length, note: 'EBS still billed', warn: true });
  const notUp = vpns.filter((v) => vpnHealth(v) !== 'up');
  if (notUp.length) {
    const nd = notUp.filter((v) => vpnHealth(v) === 'down').length;
    attn.push({ key: 'vpn_connection', label: 'VPN not fully up', count: notUp.length, note: nd + ' down · ' + (notUp.length - nd) + ' one tunnel', warn: true });
  }
  const unusedCgw = cgwList.filter((c) => !c._synthetic && !vpnsOf(c).length);
  if (unusedCgw.length) attn.push({ key: 'customer_gateway', label: 'Unused Customer GW', count: unusedCgw.length, note: 'no VPN connection', warn: true });
  const freeEni = inR('network_interface').filter((e) => e.status === 'available');
  if (freeEni.length) attn.push({ key: 'network_interface', label: 'Unattached ENI', count: freeEni.length, note: 'not attached to anything', warn: true });
  const bh = rts.filter((r) => r.blackholes > 0);
  if (bh.length) attn.push({ key: 'route_table', label: 'Blackhole routes', count: bh.reduce((s, r) => s + r.blackholes, 0), note: 'in ' + plural(bh.length, 'route table'), warn: true });
  const sickLb = lbs.filter((l) => l.targetCount === 0 || l.healthyCount < l.targetCount);
  if (sickLb.length) attn.push({ key: 'load_balancer', label: 'LB target issues', count: sickLb.length, note: 'unhealthy or no targets', warn: true });
  const badNat = inR('nat_gateway').filter((n) => n.state === 'failed');
  if (badNat.length) attn.push({ key: 'nat_gateway', label: 'Failed NAT gateway', count: badNat.length, note: 'state failed', warn: true });
  const certSoon = certs.filter((c) => c.status !== 'ISSUED' || (c.daysLeft != null && c.daysLeft <= 30));
  if (certSoon.length) attn.push({ key: 'acm_certificate', label: 'Certificate issue', count: certSoon.length, note: 'expiring ≤ 30d or not issued', warn: true });
  const unusedCert = certs.filter((c) => !c.inUse);
  if (unusedCert.length) attn.push({ key: 'acm_certificate', label: 'Unused certificate', count: unusedCert.length, note: 'not attached to any resource', warn: true });
  const pendPeer = peers.filter((pp) => pp.status !== 'active');
  if (pendPeer.length) attn.push({ key: 'vpc_peering', label: 'Peering not active', count: pendPeer.length, note: pendPeer.map((pp) => pp.status).join(', '), warn: true });
  const ssmList = inR('ssm_instance'); const ssmIds = {}; ssmList.forEach((s) => { ssmIds[s.id] = s; });
  const noSsm = ec2All.filter((i) => i.state === 'running' && !ssmIds[i.id]);
  if (noSsm.length) attn.push({ key: 'ec2_instances', label: 'EC2 without SSM', count: noSsm.length, note: 'running, not managed by SSM', warn: true });
  const lost = ssmList.filter((s) => s.pingStatus && s.pingStatus !== 'Online');
  if (lost.length) attn.push({ key: 'ssm_instance', label: 'SSM agent offline', count: lost.length, note: lost.map((s) => s.pingStatus).join(', '), warn: true });
  const patchBad = ssmList.filter((s) => (s.patchCriticalMissing || 0) + (s.patchSecurityMissing || 0) + (s.patchFailed || 0) > 0);
  if (patchBad.length) attn.push({ key: 'ssm_instance', label: 'Patch compliance', count: patchBad.length, note: 'critical/security missing or failed', warn: true });
  const neverLogs = inR('log_group').filter((g) => !g.retentionDays);
  if (neverLogs.length) attn.push({ key: 'log_group', label: 'Logs never expire', count: neverLogs.length, note: neverLogs.reduce((s, g) => s + (g.storedGiB || 0), 0).toFixed(1) + ' GiB kept forever', warn: true });
  const spSoon = all('savings_plans').filter((c) => c.daysLeft != null && c.daysLeft <= 60);
  const riSoon = all('ec2_reserved').concat(all('rds_reserved')).filter((c) => c.daysLeft != null && c.daysLeft <= 60);
  if (spSoon.length + riSoon.length) {
    const minD = Math.min.apply(null, spSoon.concat(riSoon).map((c) => c.daysLeft));
    attn.push({ key: spSoon.length ? 'savings_plans' : 'ec2_reserved', label: 'Commitment ending', count: spSoon.length + riSoon.length, note: 'within 60 days · soonest ' + minD + 'd', warn: true });
  }
  const lowUtil = all('savings_plans').concat(all('ec2_reserved'), all('rds_reserved')).filter((c) => c.utilizationPct != null && c.utilizationPct < 80);
  if (lowUtil.length) attn.push({ key: all('savings_plans').some((c) => lowUtil.includes(c)) ? 'savings_plans' : 'ec2_reserved', label: 'Under-used SP/RI', count: lowUtil.length, note: 'utilisation < 80% (30d)', warn: true });
  const overBudget = all('budgets').filter((x) => x.status === 'exceeded' || x.status === 'forecast over');
  if (overBudget.length) attn.push({ key: 'budgets', label: 'Budget at risk', count: overBudget.length, note: overBudget.filter((x) => x.status === 'exceeded').length + ' exceeded · ' + overBudget.filter((x) => x.status !== 'exceeded').length + ' forecast over', warn: true });
  addSection('NEEDS ATTENTION', attn);
  addSection('OTHER REGIONAL SERVICES · ' + region.toUpperCase(),
    REGIONAL_TILES.map(([k, l]) => ({ key: k, label: l, count: inR(k).length })).filter((t) => t.count > 0));
  addSection('GLOBAL', GLOBAL_TILES.map(([k, l]) => ({ key: k, label: l, count: all(k).length })).filter((t) => t.count > 0));

  return {
    W: W0 + K.PAD, H: ty, DH, frames, region, rects, cards, edges, gws, cgws, tiles, sections, chips, igws, stripLabels,
    cols: { hasLeft, cgwX, gwX, vpcX, top },
    stats: {
      vpc: vpcList.filter((v) => !v._synthetic).length, subnets: subnets.length, ec2: ec2.length, ec2All: ec2All.length,
      vpn: vpns.length, vpnUp: vpns.filter((v) => vpnHealth(v) === 'up').length, cgw: cgwList.length,
    },
  };
}

// ---------- export ----------
const xe = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

function toDrawio(L) {
  let n = 2; const out = []; const ids = {};
  const vtx = (key, value, style, x, y, w, h) => {
    const id = 'n' + (n++); if (key) ids[key] = id;
    out.push('<mxCell id="' + id + '" value="' + xe(value) + '" style="' + style + '" vertex="1" parent="1"><mxGeometry x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" as="geometry"/></mxCell>');
  };
  const base = 'html=1;whiteSpace=wrap;rounded=1;arcSize=5;align=left;verticalAlign=top;spacingLeft=8;spacingTop=2;fontSize=11;';
  L.rects.forEach((r) => vtx(r.id, '<b>' + xe(r.title) + '</b><br><font color="#64748b">' + xe(r.sub) + '</font>',
    base + (r.kind === 'vpc' ? 'fillColor=#f5f9ff;strokeColor=#93c5fd;fontColor=#1e3a8a;' : 'fillColor=#ffffff;strokeColor=#cbd5e1;dashed=1;'), r.x, r.y, r.w, r.h));
  L.gws.forEach((g) => vtx(g.id, '<font style="font-size:9px" color="#6366f1">' + xe(g.sub) + '</font><br><b>' + xe(g.title) + '</b><br>' + xe(g.stat),
    base + 'align=center;spacingLeft=0;fillColor=#eef2ff;strokeColor=#818cf8;fontColor=#312e81;', g.x, g.y, g.w, g.h));
  L.cgws.forEach((c) => vtx(c.id, '<b>' + xe(c.title) + '</b><br>' + xe(c.sub) +
    c.vpns.map((v) => '<br><font color="' + HEALTH[v.health] + '">●</font> ' + xe(v.name)).join(''),
    base + (c.unused ? 'fillColor=#f8fafc;strokeColor=#cbd5e1;dashed=1;' : 'fillColor=#fff7ed;strokeColor=#fdba74;fontColor=#7c2d12;'), c.x, c.y, c.w, c.h));
  (L.igws || []).forEach((g) => vtx(g.id, xe(g.label), 'html=1;rounded=1;arcSize=50;fillColor=#ecfdf5;strokeColor=#34d399;fontColor=#047857;fontSize=10;fontStyle=1;', g.x, g.y, g.w, g.h));
  (L.chips || []).forEach((c) => { const s = CHIP[c.kind] || CHIP.ep; vtx(c.id, xe(c.label), 'html=1;rounded=1;arcSize=30;fontSize=10;align=left;spacingLeft=6;fillColor=' + s[0] + ';strokeColor=' + (c.bad ? '#ef4444' : s[1]) + ';fontColor=' + s[2] + ';', c.x, c.y, c.w, c.h); });
  L.cards.forEach((c) => vtx(c.id, '<b>' + xe(c.title) + '</b><br>' + xe(c.line2) + '<br><font color="#64748b">' + xe(c.ebs + (c.pub ? '  ·  ' + (c.eip ? 'EIP ' : '') + c.pub : '')) + '</font>',
    base + 'fillColor=' + (c.state === 'running' ? '#ffffff' : '#f1f5f9') + ';strokeColor=' + (STATE_COLOR[c.state] || '#f59e0b') + ';strokeWidth=1.5;', c.x, c.y, c.w, c.h));
  L.sections.forEach((s) => vtx(null, xe(s.label), 'text;html=1;fontSize=10;fontStyle=1;fontColor=#94a3b8;align=left;verticalAlign=bottom;', s.x, s.y - 14, 400, 16));
  L.tiles.forEach((t) => vtx(null, '<b style="font-size:16px">' + t.count + '</b><br>' + xe(t.label) + (t.note ? '<br><font color="#94a3b8" style="font-size:9px">' + xe(t.note) + '</font>' : ''),
    base + (t.warn ? 'fillColor=#fffbeb;strokeColor=#fcd34d;' : 'fillColor=#f8fafc;strokeColor=#e2e8f0;'), t.x, t.y, t.w, t.h));
  L.edges.forEach((e, i) => {
    if (!ids[e.from] || !ids[e.to]) return;
    out.push('<mxCell id="e' + i + '" value="" style="endArrow=none;html=1;curved=1;strokeWidth=' + e.width + ';strokeColor=' + e.color + ';' + (e.dash ? 'dashed=1;' : '') +
      '" edge="1" parent="1" source="' + ids[e.from] + '" target="' + ids[e.to] + '"><mxGeometry relative="1" as="geometry"/></mxCell>');
  });
  return '<mxfile host="indomas-it"><diagram name="AWS ' + xe(L.region) + '"><mxGraphModel grid="1" gridSize="10" page="0"><root><mxCell id="0"/><mxCell id="1" parent="0"/>' +
    out.join('') + '</root></mxGraphModel></diagram></mxfile>';
}

function download(blob, name) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob); a.download = name;
  document.body.appendChild(a); a.click();
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 800);
}

// ---------- komponen ----------
// Warna ikon ala AWS Architecture Icons (compute oranye, networking ungu).
const IC = { ec2: '#ea580c', ec2Off: '#94a3b8', nat: '#7c3aed', vpc: '#7c3aed', vgw: '#4f46e5', tgw: '#7c3aed', cgw: '#c2410c', cgwOff: '#94a3b8', igw: '#059669', os: '#0d9488' };
const SUBNET_TONE = {
  public: { icon: '#16a34a', head: '#f0fdf4', stroke: '#86efac' },
  'private-nat': { icon: '#2563eb', head: '#eff6ff', stroke: '#bfdbfe' },
  'private-nat-instance': { icon: '#2563eb', head: '#eff6ff', stroke: '#bfdbfe' },
  'private-other': { icon: '#2563eb', head: '#eff6ff', stroke: '#bfdbfe' },
  private: { icon: '#64748b', head: '#f8fafc', stroke: '#cbd5e1' },
  none: { icon: '#94a3b8', head: '#f8fafc', stroke: '#cbd5e1' },
};
const W_ = { stroke: '#fff', fill: 'none', strokeLinecap: 'round', strokeLinejoin: 'round' };
const GLYPH = {
  db: (
    <g>
      <ellipse cx="12" cy="7.2" rx="5.6" ry="2.2" {...W_} strokeWidth="1.5" />
      <path d="M6.4 7.2v9.6c0 1.2 2.5 2.2 5.6 2.2s5.6-1 5.6-2.2V7.2M6.4 12c0 1.2 2.5 2.2 5.6 2.2s5.6-1 5.6-2.2" {...W_} strokeWidth="1.5" />
    </g>
  ),
  ec2: (
    <g>
      <rect x="7.5" y="7.5" width="9" height="9" rx="1.6" {...W_} strokeWidth="1.6" />
      <path d="M10 4.8v2.2M14 4.8v2.2M10 17v2.2M14 17v2.2M4.8 10h2.2M4.8 14h2.2M17 10h2.2M17 14h2.2" {...W_} strokeWidth="1.4" />
    </g>
  ),
  vpc: <path d="M8 17.2h8.6a3.3 3.3 0 0 0 .5-6.55A4.9 4.9 0 0 0 7.8 9.5 3.9 3.9 0 0 0 8 17.2z" {...W_} strokeWidth="1.6" />,
  subnet: (
    <g fill="#fff">
      <rect x="6" y="6" width="5" height="5" rx="1.2" /><rect x="13" y="6" width="5" height="5" rx="1.2" />
      <rect x="6" y="13" width="5" height="5" rx="1.2" /><rect x="13" y="13" width="5" height="5" rx="1.2" fillOpacity="0.55" />
    </g>
  ),
  vgw: (
    <g>
      <rect x="6.8" y="11" width="10.4" height="7.8" rx="1.6" fill="#fff" />
      <path d="M9.2 11V8.9a2.8 2.8 0 0 1 5.6 0V11" {...W_} strokeWidth="1.7" />
    </g>
  ),
  tgw: (
    <g>
      <path d="M12 5.8v3.6M12 14.6v3.6M5.8 12h3.6M14.6 12h3.6" {...W_} strokeWidth="1.6" />
      <circle cx="12" cy="12" r="2.7" fill="#fff" />
      <circle cx="12" cy="5.2" r="1.5" fill="#fff" /><circle cx="12" cy="18.8" r="1.5" fill="#fff" />
      <circle cx="5.2" cy="12" r="1.5" fill="#fff" /><circle cx="18.8" cy="12" r="1.5" fill="#fff" />
    </g>
  ),
  cgw: (
    <g>
      <path d="M7 18.5V6.5h6.5v12M13.5 10h3.5v8.5M5.5 18.5h13" {...W_} strokeWidth="1.5" />
      <path d="M9.2 9h.1M11.3 9h.1M9.2 12h.1M11.3 12h.1M9.2 15h.1M11.3 15h.1" {...W_} strokeWidth="1.7" />
    </g>
  ),
  igw: (
    <g>
      <circle cx="12" cy="12" r="6.3" {...W_} strokeWidth="1.5" />
      <ellipse cx="12" cy="12" rx="2.7" ry="6.3" {...W_} strokeWidth="1.3" />
      <path d="M5.9 12h12.2" {...W_} strokeWidth="1.3" />
    </g>
  ),
  nat: <path d="M6 9.4h11l-2.6-2.6M18 14.6H7l2.6 2.6" {...W_} strokeWidth="1.6" />,
  cloud: <path d="M8 17.2h8.6a3.3 3.3 0 0 0 .5-6.55A4.9 4.9 0 0 0 7.8 9.5 3.9 3.9 0 0 0 8 17.2z" {...W_} strokeWidth="1.6" />,
};
function Glyph({ kind, x, y, s = 24, color }) {
  return (
    <g transform={'translate(' + x + ' ' + y + ') scale(' + (s / 24) + ')'}>
      <rect width="24" height="24" rx="6" fill={color} />
      {GLYPH[kind]}
    </g>
  );
}
// Persegi dengan sudut atas membulat saja (header subnet).
const topRound = (x, y, w, h, r) => 'M' + x + ' ' + (y + h) + 'V' + (y + r) + 'Q' + x + ' ' + y + ' ' + (x + r) + ' ' + y +
  'H' + (x + w - r) + 'Q' + (x + w) + ' ' + y + ' ' + (x + w) + ' ' + (y + r) + 'V' + (y + h) + 'Z';
// Ukur lebar teks sebenarnya (canvas) supaya potongan '…' pas dengan ruang yang ada.
let MCTX = null;
const textW = (s, size, bold) => {
  try { if (!MCTX) MCTX = document.createElement('canvas').getContext('2d'); MCTX.font = (bold ? '700 ' : '400 ') + size + 'px ' + FONT; return MCTX.measureText(String(s)).width; }
  catch (e) { return String(s).length * size * 0.56; }
};
const fit = (s, maxW, size, bold) => {
  s = String(s == null ? '' : s);
  if (textW(s, size, bold) <= maxW) return s;
  let lo = 0; let hi = s.length;
  while (lo < hi) { const m = (lo + hi + 1) >> 1; if (textW(s.slice(0, m) + '…', size, bold) <= maxW) lo = m; else hi = m - 1; }
  return s.slice(0, lo) + '…';
};
const endsOf = (d) => { const n = String(d).match(/-?[\d.]+/g) || []; return n.length >= 8 ? [+n[0], +n[1], +n[6], +n[7]] : null; };

function Legend() {
  const Line = ({ c, dash }) => (
    <svg width="26" height="8"><line x1="0" y1="4" x2="26" y2="4" stroke={c} strokeWidth="2.5" strokeDasharray={dash || undefined} strokeLinecap="round" /></svg>
  );
  const Dot = ({ c }) => <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: c }} />;
  const Sq = ({ c }) => <span className="inline-block w-3 h-3 rounded-[4px]" style={{ background: c }} />;
  const Tag = ({ cls, children }) => <span className={'px-1 rounded text-[10px] font-semibold ' + cls}>{children}</span>;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-slate-500">
      <span className="flex items-center gap-1.5"><Line c={HEALTH.up} /> VPN, all tunnels up</span>
      <span className="flex items-center gap-1.5"><Line c={HEALTH.partial} /> one tunnel up</span>
      <span className="flex items-center gap-1.5"><Line c={HEALTH.down} dash="6 4" /> down</span>
      <span className="flex items-center gap-1.5"><Line c="#818cf8" /> VGW attachment</span>
      <span className="flex items-center gap-1.5"><Line c="#a78bfa" /> TGW attachment</span>
      <span className="flex items-center gap-1.5"><Sq c={SUBNET_TONE.public.icon} /> public</span>
      <span className="flex items-center gap-1.5"><Sq c={SUBNET_TONE['private-nat'].icon} /> private</span>
      <span className="flex items-center gap-1.5"><Sq c={SUBNET_TONE.private.icon} /> isolated subnet</span>
      <span className="flex items-center gap-1.5"><Sq c={IC.ec2} /> EC2</span>
      <span className="flex items-center gap-1.5"><Sq c={IC.nat} /> NAT gateway</span>
      <span className="flex items-center gap-1.5"><Sq c={IC.os} /> OpenSearch</span>
      <span className="flex items-center gap-1.5"><Tag cls="bg-emerald-50 text-emerald-700">SSM</Tag><Tag cls="bg-slate-100 text-slate-400">no SSM</Tag> Systems Manager</span>
      <span className="flex items-center gap-1.5"><Dot c={STATE_COLOR.running} /> running</span>
      <span className="flex items-center gap-1.5"><Dot c={STATE_COLOR.stopped} /> stopped</span>
      <span className="flex items-center gap-1.5"><Tag cls="bg-emerald-50 text-emerald-700">IP</Tag> Elastic IP</span>
      <span className="flex items-center gap-1.5"><Tag cls="bg-brand-50 text-brand-700">IP</Tag> public IP</span>
    </div>
  );
}

function StatPill({ label, value, sub, tone }) {
  const tones = { ok: 'text-emerald-600', warn: 'text-amber-600', none: 'text-slate-400' };
  return (
    <div className="flex items-baseline gap-1.5 px-3 py-1.5 rounded-xl bg-slate-50 border border-slate-200/80">
      <span className="text-base font-bold text-slate-800 leading-none">{value}</span>
      <span className="text-[11px] font-medium text-slate-500">{label}</span>
      {sub && <span className={'text-[11px] font-semibold ' + (tones[tone] || tones.none)}>{sub}</span>}
    </div>
  );
}

export default function AwsTopologyPanel({ byService, accountId, lastRefreshed, onOpenResource, onSelectService, onRefresh, refreshing }) {
  const bs = byService || {};
  const regions = useMemo(() => topologyRegions(bs), [bs]);
  const [region, setRegion] = useState(null);
  const [showStopped, setShowStopped] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [auto, setAuto] = useState(true);
  const [full, setFull] = useState(false);
  const [hov, setHov] = useState(null);
  const [q, setQ] = useState('');
  const svgRef = useRef(null);
  const boxRef = useRef(null);
  const zoomRef = useRef(1);
  const dragRef = useRef({ down: false, moved: false });
  zoomRef.current = zoom;

  useEffect(() => {
    if (!region || !regions.includes(region)) setRegion(regions[0] || null);
  }, [regions, region]);

  const L = useMemo(() => (region ? buildTopology(bs, region, { showStopped }) : null), [bs, region, showStopped]);
  const DH = L ? (L.DH || L.H) : 0;
  const clampZ = (z) => Math.max(0.3, Math.min(2, Math.round(z * 100) / 100));

  // Auto-fit: ikuti lebar kotak diagram (sidebar dibuka/ditutup, resize jendela, mode layar penuh).
  useEffect(() => {
    const el = boxRef.current;
    if (!auto || !L || !el) return undefined;
    const apply = () => {
      const wz = (el.clientWidth - 24) / L.W;
      setZoom(Math.max(0.3, Math.min(full ? 1.6 : 1.25, Math.round(wz * 100) / 100)));
    };
    apply();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [auto, L, DH, full]);

  // Ctrl/Cmd + scroll = zoom di titik kursor.
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const cur = zoomRef.current;
      const next = clampZ(cur * (e.deltaY < 0 ? 1.1 : 1 / 1.1));
      if (next === cur) return;
      const r = el.getBoundingClientRect();
      const ox = e.clientX - r.left; const oy = e.clientY - r.top;
      const px = (el.scrollLeft + ox) / cur; const py = (el.scrollTop + oy) / cur;
      setAuto(false); setZoom(next);
      requestAnimationFrame(() => { el.scrollLeft = px * next - ox; el.scrollTop = py * next - oy; });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [L]);

  useEffect(() => {
    if (!full) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setFull(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [full]);

  // Geser diagram dengan drag (klik tetap membuka detail kalau tidak digeser).
  const onPointerDown = (e) => {
    if (e.button !== 0 || !boxRef.current) return;
    const el = boxRef.current;
    const d = { down: true, moved: false, x: e.clientX, y: e.clientY, sl: el.scrollLeft, st: el.scrollTop };
    dragRef.current = d;
    const move = (ev) => {
      const dx = ev.clientX - d.x; const dy = ev.clientY - d.y;
      if (!d.moved && Math.abs(dx) + Math.abs(dy) < 5) return;
      d.moved = true;
      el.scrollLeft = d.sl - dx; el.scrollTop = d.st - dy;
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      d.down = false;
      setTimeout(() => { d.moved = false; }, 0);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const zoomBy = (dz) => { setAuto(false); setZoom((z) => clampZ(z + dz)); };
  const ql = q.trim().toLowerCase();
  const hit = (s) => !ql || String(s || '').includes(ql);
  const matchCount = ql && L ? L.cards.filter((c) => hit(c.search)).length + L.cgws.filter((c) => hit(c.search)).length + L.gws.filter((g) => hit(g.search)).length : 0;
  const open = (svc, row) => { if (dragRef.current.moved) return; if (svc && row && onOpenResource) onOpenResource(svc, row); };
  const fileBase = 'aws-topology-' + (accountId || 'account') + '-' + (region || '') + '-' + new Date().toISOString().slice(0, 10);
  const hoverOn = (id) => ({ onMouseEnter: () => setHov(id), onMouseLeave: () => setHov((h) => (h === id ? null : h)) });

  const svgText = () => {
    const node = svgRef.current.cloneNode(true);
    node.setAttribute('width', L.W); node.setAttribute('height', L.H);
    node.setAttribute('viewBox', '0 0 ' + L.W + ' ' + L.H);
    node.querySelectorAll('[opacity]').forEach((el) => el.removeAttribute('opacity'));
    return '<?xml version="1.0" encoding="UTF-8"?>\n' + new XMLSerializer().serializeToString(node);
  };
  const exportSvg = () => download(new Blob([svgText()], { type: 'image/svg+xml' }), fileBase + '.svg');
  const exportPng = () => {
    const img = new Image();
    img.onload = () => {
      const s = 2; const cv = document.createElement('canvas');
      cv.width = L.W * s; cv.height = L.H * s;
      const ctx = cv.getContext('2d'); ctx.scale(s, s); ctx.drawImage(img, 0, 0);
      cv.toBlob((b) => b && download(b, fileBase + '.png'), 'image/png');
    };
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgText());
  };
  const exportDrawio = () => download(new Blob([toDrawio(L)], { type: 'application/xml' }), fileBase + '.drawio');

  if (!regions.length) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-sm text-slate-500">
        No network resources in this snapshot yet. Run "Refresh from AWS" first.
      </div>
    );
  }

  const btn = 'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition';
  const st = L ? L.stats : {};
  const tileGroups = L ? L.sections.map((s) => ({ label: s.label, items: L.tiles.filter((t) => t.sec === s.label) })).filter((g) => g.items.length) : [];
  const edgeTouches = (e) => hov && (e.from === hov || e.to === hov);

  return (
    <div className="space-y-3">
      {/* Filter + ringkasan */}
      <div className="bg-white rounded-2xl border border-slate-200 p-3.5 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 bg-slate-100/80 rounded-xl p-1">
            {regions.map((r) => (
              <button key={r} onClick={() => setRegion(r)}
                className={'px-3 py-1 rounded-lg text-xs font-medium transition ' + (r === region ? 'bg-white text-brand-700 shadow-sm ring-1 ring-slate-200' : 'text-slate-500 hover:text-slate-700')}>
                {r}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" style={{ width: 14, height: 14 }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Highlight name, IP, SG..."
              className="pl-8 pr-7 py-1.5 w-60 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-200" />
            {q && (
              <button onClick={() => setQ('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X style={{ width: 13, height: 13 }} />
              </button>
            )}
          </div>
          {ql && <span className="text-xs text-slate-500">{matchCount} match{matchCount === 1 ? '' : 'es'}</span>}
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none">
            <input type="checkbox" checked={showStopped} onChange={(e) => setShowStopped(e.target.checked)} className="rounded" />
            Show stopped instances
          </label>
          <div className="flex-1" />
          <div className="flex flex-wrap items-center gap-1.5">
            <StatPill label={st.vpc === 1 ? 'VPC' : 'VPCs'} value={st.vpc} />
            <StatPill label="subnets" value={st.subnets} />
            <StatPill label="EC2" value={st.ec2} sub={st.ec2 !== st.ec2All ? 'of ' + st.ec2All : ''} />
            <StatPill label="customer GW" value={st.cgw} />
            <StatPill label="VPN" value={st.vpn} sub={st.vpn ? st.vpnUp + ' up' : ''} tone={st.vpnUp === st.vpn ? 'ok' : 'warn'} />
          </div>
        </div>
      </div>

      {/* Diagram */}
      <div className={full
        ? 'fixed inset-0 z-[70] bg-white flex flex-col'
        : 'bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col'}>
        <div className="flex flex-wrap items-center gap-2 px-3.5 py-2 border-b border-slate-100 bg-slate-50/60">
          <span className="text-xs font-semibold text-slate-700">{'Topology · ' + (region || '')}</span>
          <span className="hidden md:inline text-[11px] text-slate-400">Drag to pan · Ctrl + scroll to zoom · click a box for details</span>
          <div className="flex-1" />
          {onRefresh && (
            <button className={btn + (refreshing ? ' opacity-60 cursor-wait' : '')} onClick={onRefresh} disabled={refreshing}
              title={'Fetch the latest state from AWS (VPN tunnels, EC2, subnets…)' + (lastRefreshed ? ' · last: ' + new Date(lastRefreshed).toLocaleString('en-GB') : '')}>
              <RefreshCw style={{ width: 14, height: 14 }} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          )}
          <div className="flex items-center gap-1">
            <button className={btn} onClick={() => zoomBy(-0.1)} title="Zoom out"><ZoomOut style={{ width: 14, height: 14 }} /></button>
            <button className={btn + ' w-14 justify-center'} onClick={() => setAuto(true)} title="Fit to window">{auto ? 'Fit' : Math.round(zoom * 100) + '%'}</button>
            <button className={btn} onClick={() => zoomBy(0.1)} title="Zoom in"><ZoomIn style={{ width: 14, height: 14 }} /></button>
            <button className={btn} onClick={() => { setFull((f) => !f); setAuto(true); }} title={full ? 'Exit full screen (Esc)' : 'Full screen'}>
              {full ? <Minimize2 style={{ width: 14, height: 14 }} /> : <Maximize2 style={{ width: 14, height: 14 }} />}
              <span className="hidden sm:inline">{full ? 'Exit' : 'Full screen'}</span>
            </button>
          </div>
          <div className="flex items-center gap-1">
            <button className={btn} onClick={exportPng}><ImageIcon style={{ width: 14, height: 14 }} /> PNG</button>
            <button className={btn} onClick={exportSvg}><Download style={{ width: 14, height: 14 }} /> SVG</button>
            <button className={btn} onClick={exportDrawio}><FileText style={{ width: 14, height: 14 }} /> draw.io</button>
          </div>
        </div>

        <div ref={boxRef} onPointerDown={onPointerDown}
          className={'overflow-auto select-none ' + (full ? 'flex-1' : '')}
          style={{ maxHeight: full ? 'none' : '80vh', cursor: 'grab', background: '#f8fafc' }}>
          {L && (
            <svg ref={svgRef} xmlns="http://www.w3.org/2000/svg" width={L.W * zoom} height={DH * zoom}
              viewBox={'0 0 ' + L.W + ' ' + DH} fontFamily={FONT} style={{ display: 'block', margin: '0 auto' }}>
              <defs>
                <pattern id="tp-dots" width="18" height="18" patternUnits="userSpaceOnUse">
                  <circle cx="1.2" cy="1.2" r="1.1" fill="#e2e8f0" />
                </pattern>
                <filter id="tp-sh" x="-10%" y="-20%" width="120%" height="150%">
                  <feDropShadow dx="0" dy="1.5" stdDeviation="2" floodColor="#0f172a" floodOpacity="0.10" />
                </filter>
              </defs>
              <rect x="0" y="0" width={L.W} height={L.H} fill="#ffffff" />
              <rect x="0" y="0" width={L.W} height={L.H} fill="url(#tp-dots)" />
              <text x={K.PAD} y={K.PAD + 16} fontSize="18" fontWeight="700" fill="#0f172a">{'AWS topology · ' + L.region}</text>
              <text x={K.PAD} y={K.PAD + 35} fontSize="11.5" fill="#64748b">
                {['Account ' + (accountId || '-'), lastRefreshed ? 'snapshot ' + new Date(lastRefreshed).toLocaleString('en-GB') : ''].filter(Boolean).join(' · ')}
              </text>

              {(L.frames || []).map((f) => (
                <g key={'f' + f.kind}>
                  <rect x={f.x} y={f.y} width={f.w} height={f.h} rx="18"
                    fill={f.kind === 'region' ? '#fdfcff' : '#fbfbfc'} stroke={f.kind === 'region' ? '#c4b5fd' : '#cbd5e1'}
                    strokeWidth="1.4" strokeDasharray="7 5" />
                  <Glyph kind={f.kind === 'region' ? 'cloud' : 'cgw'} x={f.x + 12} y={f.y + 10} s={22} color={f.kind === 'region' ? '#7c3aed' : '#475569'} />
                  <text x={f.x + 42} y={f.y + 26} fontSize="12.5" fontWeight="700" fill={f.kind === 'region' ? '#5b21b6' : '#334155'}>{f.label}</text>
                  {f.sub && <text x={f.x + f.w - 14} y={f.y + 26} fontSize="10.5" fill="#94a3b8" textAnchor="end">{f.sub}</text>}
                </g>
              ))}

              {L.edges.map((e) => {
                const dim = (ql && e.kind === 'vpn' && !hit(e.search)) || (hov && !edgeTouches(e));
                const strong = edgeTouches(e);
                const ends = endsOf(e.d);
                const flowing = e.kind === 'attach' || (e.row && vpnHealth(e.row) !== 'down');
                return (
                  <g key={'e' + e.id} opacity={dim ? 0.15 : 1} style={{ cursor: e.row ? 'pointer' : 'default' }}
                    onClick={() => e.row && open('vpn_connection', e.row)}>
                    <title>{e.tip}</title>
                    <path d={e.d} fill="none" stroke={e.color} strokeWidth={e.width + 7} strokeOpacity={strong ? 0.22 : 0.1} strokeLinecap="round" />
                    <path d={e.d} fill="none" stroke={e.color} strokeWidth={strong ? e.width + 1 : e.width}
                      strokeDasharray={e.dash || undefined} strokeLinecap="round" />
                    {flowing && (
                      <path d={e.d} fill="none" stroke="#ffffff" strokeWidth="1.6" strokeDasharray="2 14" strokeLinecap="round">
                        <animate attributeName="stroke-dashoffset" from="32" to="0" dur="1.4s" repeatCount="indefinite" />
                      </path>
                    )}
                    {ends && (
                      <g>
                        <circle cx={ends[0]} cy={ends[1]} r="3.6" fill="#ffffff" stroke={e.color} strokeWidth="2" />
                        <circle cx={ends[2]} cy={ends[3]} r="3.6" fill="#ffffff" stroke={e.color} strokeWidth="2" />
                      </g>
                    )}
                  </g>
                );
              })}

              {L.rects.map((r) => {
                if (r.kind === 'vpc') {
                  const statW = textW(r.stat, 10.5, true) + 20;
                  return (
                    <g key={'r' + r.id} onClick={() => open(r.svc, r.row)} style={{ cursor: r.svc ? 'pointer' : 'default' }} {...hoverOn(r.id)}>
                      <title>{r.tip}</title>
                      <rect x={r.x} y={r.y} width={r.w} height={r.h} rx="16" fill="#faf8ff" stroke={hov === r.id ? '#7c3aed' : '#a78bfa'} strokeWidth={hov === r.id ? 2 : 1.5} />
                      <Glyph kind="vpc" x={r.x + 14} y={r.y + 14} s={28} color={IC.vpc} />
                      <text x={r.x + 52} y={r.y + 27} fontSize="14" fontWeight="700" fill="#3b0764">{fit(r.title, r.w - 52 - 20, 14, true)}</text>
                      <text x={r.x + 52} y={r.y + 44} fontSize="11" fill="#6b7280">{fit(r.sub, r.w - 52 - statW - 26, 11)}</text>
                      <rect x={r.x + r.w - statW - 14} y={r.y + 30} width={statW} height="20" rx="10" fill="#ede9fe" />
                      <text x={r.x + r.w - 14 - statW / 2} y={r.y + 44} fontSize="10.5" fontWeight="600" fill="#6d28d9" textAnchor="middle">{r.stat}</text>
                      {r.empty && <text x={r.x + 16} y={r.y + r.h - 14} fontSize="11" fontStyle="italic" fill="#94a3b8">No subnets in inventory</text>}
                    </g>
                  );
                }
                const tone = SUBNET_TONE[r.exposure] || SUBNET_TONE.none;
                const ex = r.exposure && EXPO[r.exposure];
                const bw = ex ? textW(ex[0], 9, true) + ex[0].length * 0.4 + 14 : 0;
                return (
                  <g key={'r' + r.id} onClick={(ev) => { ev.stopPropagation(); open(r.svc, r.row); }} style={{ cursor: r.svc ? 'pointer' : 'default' }}>
                    <title>{r.tip}</title>
                    <rect x={r.x} y={r.y} width={r.w} height={r.h} rx="12" fill="#ffffff" stroke={tone.stroke} strokeWidth="1.2" strokeDasharray={r.synthetic ? '4 3' : undefined} />
                    <path d={topRound(r.x + 0.6, r.y + 0.6, r.w - 1.2, K.SUB_HEAD - 10, 11.5)} fill={tone.head} />
                    <line x1={r.x + 0.6} y1={r.y + K.SUB_HEAD - 9.4} x2={r.x + r.w - 0.6} y2={r.y + K.SUB_HEAD - 9.4} stroke={tone.stroke} strokeOpacity="0.6" />
                    <Glyph kind="subnet" x={r.x + 10} y={r.y + 11} s={24} color={tone.icon} />
                    <text x={r.x + 42} y={r.y + 22} fontSize="12" fontWeight="700" fill="#1e293b">{fit(r.title, r.w - 42 - bw - 16, 12, true)}</text>
                    {ex && (
                      <g>
                        <rect x={r.x + r.w - bw - 9} y={r.y + 10} width={bw} height="16" rx="5" fill={ex[1]} stroke={tone.stroke} strokeOpacity="0.7" />
                        <text x={r.x + r.w - 9 - bw / 2} y={r.y + 21.5} fontSize="9" fontWeight="700" fill={ex[2]} textAnchor="middle" letterSpacing="0.4">{ex[0]}</text>
                      </g>
                    )}
                    <text x={r.x + 42} y={r.y + 38} fontSize="10.5" fill="#64748b">{fit(r.sub, r.w - 52, 10.5)}</text>
                    {r.empty && <text x={r.x + r.w / 2} y={r.y + K.SUB_HEAD + 12} fontSize="10.5" fontStyle="italic" fill="#94a3b8" textAnchor="middle">no instances</text>}
                  </g>
                );
              })}

              {L.gws.map((g) => (
                <g key={'g' + g.id} onClick={() => open(g.svc, g.row)} style={{ cursor: g.svc ? 'pointer' : 'default' }} opacity={hit(g.search) ? 1 : 0.25} {...hoverOn(g.id)}>
                  <title>{g.tip}</title>
                  <rect x={g.x} y={g.y} width={g.w} height={g.h} rx="14" fill="#eef2ff" stroke={hov === g.id ? '#4f46e5' : '#a5b4fc'} strokeWidth={hov === g.id ? 2 : 1.4} filter="url(#tp-sh)" />
                  <Glyph kind={g.kind === 'tgw' ? 'tgw' : 'vgw'} x={g.x + 12} y={g.y + (g.h - 32) / 2} s={32} color={g.kind === 'tgw' ? IC.tgw : IC.vgw} />
                  <text x={g.x + 54} y={g.y + 22} fontSize="8.5" fontWeight="700" fill="#6366f1" letterSpacing="0.5">{g.sub}</text>
                  <text x={g.x + 54} y={g.y + 40} fontSize="13" fontWeight="700" fill="#312e81">{fit(g.title, g.w - 62, 13, true)}</text>
                  <text x={g.x + 54} y={g.y + 56} fontSize="10.5" fill="#64748b">{g.stat}</text>
                </g>
              ))}

              {L.cgws.map((c) => (
                <g key={'c' + c.id} onClick={() => open(c.svc, c.row)} style={{ cursor: c.svc ? 'pointer' : 'default' }} opacity={hit(c.search) ? 1 : 0.25} {...hoverOn(c.id)}>
                  <title>{c.tip}</title>
                  <rect x={c.x} y={c.y} width={c.w} height={c.h} rx="12" fill={c.unused ? '#f8fafc' : '#fff7ed'}
                    stroke={c.unused ? '#cbd5e1' : (hov === c.id ? '#ea580c' : '#fdba74')} strokeDasharray={c.unused ? '4 3' : undefined}
                    strokeWidth={(ql && hit(c.search)) || hov === c.id ? 2 : 1.3} filter={c.unused ? undefined : 'url(#tp-sh)'} />
                  <Glyph kind="cgw" x={c.x + 10} y={c.y + 10} s={28} color={c.unused ? IC.cgwOff : IC.cgw} />
                  <text x={c.x + 46} y={c.y + 23} fontSize="12.5" fontWeight="700" fill={c.unused ? '#64748b' : '#7c2d12'}>{fit(c.title, c.w - 56, 12.5, true)}</text>
                  <text x={c.x + 46} y={c.y + 39} fontSize="10.5" fill={c.unused ? '#94a3b8' : '#9a3412'}>{fit(c.sub || c.id, c.w - 56, 10.5)}</text>
                  {c.vpns.map((v) => (
                    <g key={v.id} onClick={(ev) => { ev.stopPropagation(); open('vpn_connection', v.row); }} style={{ cursor: 'pointer' }}>
                      <title>{v.tip}</title>
                      <rect x={c.x + 8} y={v.y - 2} width={c.w - 16} height="19" rx="6" fill="#ffffff" stroke="#fed7aa" />
                      {(v.tunnels.length ? v.tunnels : ['?']).map((t, k) => (
                        <circle key={k} cx={c.x + 19 + k * 10} cy={v.y + 7.5} r="3.6" fill={t === 'UP' ? HEALTH.up : t === '?' ? HEALTH.unknown : HEALTH.down} />
                      ))}
                      <text x={c.x + 23 + Math.max(1, v.tunnels.length) * 10} y={v.y + 11.5} fontSize="10.5" fontWeight="500" fill="#334155">{fit(v.name, c.w - 40 - Math.max(1, v.tunnels.length) * 10, 10.5)}</text>
                    </g>
                  ))}
                </g>
              ))}

              {L.cards.map((c) => {
                const on = hit(c.search); const osd = c.kind === 'os'; const nat = c.kind === 'nat'; const sc = nat ? IC.nat : (STATE_COLOR[c.state] || '#f59e0b');
                const running = c.state === 'running';
                const badge = c.pub || '';
                const pbw = badge ? textW(badge, 9.5, true) + 12 : 0;
                const tone = c.badge ? (BADGE[c.badgeTone] || BADGE.none) : null;
                const sbw = c.badge ? textW(c.badge, 9, true) + 12 : 0;
                return (
                  <g key={'i' + c.id} onClick={(ev) => { ev.stopPropagation(); open(c.svc, c.row); }} style={{ cursor: 'pointer' }} opacity={on ? 1 : 0.2}>
                    <title>{c.tip + (c.ssmTip || '')}</title>
                    <rect x={c.x} y={c.y} width={c.w} height={c.h} rx="10" fill={osd ? '#f0fdfa' : nat ? '#faf5ff' : running ? '#ffffff' : '#f8fafc'}
                      stroke={ql && on ? '#2563eb' : osd ? '#99f6e4' : nat ? '#ddd6fe' : '#e2e8f0'} strokeWidth={ql && on ? 2 : 1} filter="url(#tp-sh)" />
                    <Glyph kind={osd ? 'db' : nat ? 'nat' : 'ec2'} x={c.x + 10} y={c.y + 10} s={26} color={osd ? IC.os : nat ? IC.nat : running ? IC.ec2 : IC.ec2Off} />
                    <circle cx={c.x + 34} cy={c.y + 34} r="5" fill={sc} stroke="#ffffff" strokeWidth="2" />
                    <text x={c.x + 44} y={c.y + 21} fontSize="12" fontWeight="700" fill={running ? '#0f172a' : '#64748b'}>{fit(c.title, c.w - 44 - sbw - 14, 12, true)}</text>
                    {c.badge && (
                      <g>
                        <rect x={c.x + c.w - sbw - 8} y={c.y + 8} width={sbw} height="15" rx="5" fill={tone[0]} />
                        <text x={c.x + c.w - 8 - sbw / 2} y={c.y + 18.8} fontSize="9" fontWeight="700" fill={tone[1]} textAnchor="middle">{c.badge}</text>
                      </g>
                    )}
                    <text x={c.x + 44} y={c.y + 38} fontSize="11" fill="#475569">{fit(c.line2, c.w - 52, 11)}</text>
                    <text x={c.x + 44} y={c.y + 55} fontSize="10.5" fill="#64748b">{fit(c.ebs, c.w - 44 - (badge ? pbw + 14 : 8), 10.5)}</text>
                    {badge && (
                      <g>
                        <rect x={c.x + c.w - pbw - 8} y={c.y + 44} width={pbw} height="15" rx="5" fill={c.eip ? '#ecfdf5' : '#eff6ff'} />
                        <text x={c.x + c.w - 8 - pbw / 2} y={c.y + 54.8} fontSize="9.5" fontWeight="600" fill={c.eip ? '#047857' : '#1d4ed8'} textAnchor="middle">{badge}</text>
                      </g>
                    )}
                  </g>
                );
              })}

              {L.igws.map((g) => (
                <g key={'w' + g.id} onClick={() => open(g.svc, g.row)} style={{ cursor: 'pointer' }}>
                  <title>{g.row.id + ' — Internet Gateway attached to this VPC'}</title>
                  <rect x={g.x} y={g.y - 1} width={g.w} height={g.h + 2} rx="12" fill="#ecfdf5" stroke="#34d399" filter="url(#tp-sh)" />
                  <Glyph kind="igw" x={g.x + 4} y={g.y + 1} s={20} color={IC.igw} />
                  <text x={g.x + 30} y={g.y + 15} fontSize="10.5" fontWeight="700" fill="#047857">{fit(g.label, g.w - 38, 10.5, true)}</text>
                </g>
              ))}
              {L.stripLabels.map((s) => (
                <text key={'sl' + s.id} x={s.x} y={s.y} fontSize="9.5" fontWeight="700" fill="#8b5cf6" letterSpacing="0.6">{s.label}</text>
              ))}
              {L.chips.map((ch) => {
                const cs = CHIP[ch.kind] || CHIP.ep;
                return (
                  <g key={'ch' + ch.id} onClick={(ev) => { ev.stopPropagation(); open(ch.svc, ch.row); }} style={{ cursor: 'pointer' }} opacity={hit(ch.search) ? 1 : 0.25}>
                    <title>{ch.tip}</title>
                    <rect x={ch.x} y={ch.y} width={ch.w} height={ch.h} rx="8" fill={cs[0]} stroke={ch.bad ? '#ef4444' : cs[1]} strokeWidth={ch.bad ? 1.5 : 1} />
                    <circle cx={ch.x + 11} cy={ch.y + ch.h / 2} r="3" fill={ch.bad ? '#ef4444' : cs[2]} />
                    <text x={ch.x + 20} y={ch.y + ch.h / 2 + 3.8} fontSize="10.5" fontWeight="600" fill={ch.bad ? '#b91c1c' : cs[2]}>{ch.label}</text>
                  </g>
                );
              })}

              {/* Tile ringkasan: di luar viewBox layar (ditampilkan sebagai kartu HTML di bawah), ikut saat ekspor. */}
              {L.sections.map((s) => (
                <text key={'s' + s.label} x={s.x} y={s.y} fontSize="10" fontWeight="700" fill="#94a3b8" letterSpacing="0.6">{s.label}</text>
              ))}
              {L.tiles.map((t) => (
                <g key={'t' + t.sec + t.label}>
                  <rect x={t.x} y={t.y} width={t.w} height={t.h} rx="10" fill={t.warn ? '#fffbeb' : '#ffffff'} stroke={t.warn ? '#fcd34d' : '#e2e8f0'} />
                  <text x={t.x + 12} y={t.y + 24} fontSize="18" fontWeight="700" fill={t.warn ? '#b45309' : '#0f172a'}>{t.count}</text>
                  <text x={t.x + 12} y={t.y + 40} fontSize="10.5" fontWeight="600" fill="#475569">{trunc(t.label, 26)}</text>
                  {t.note && <text x={t.x + 12} y={t.y + 52} fontSize="9" fill="#94a3b8">{trunc(t.note, 30)}</text>}
                </g>
              ))}
            </svg>
          )}
        </div>

        <div className="px-3.5 py-2 border-t border-slate-100 bg-white">
          <Legend />
        </div>
      </div>

      {/* Ringkasan layanan lain (HTML supaya tetap terbaca di layar sempit) */}
      {tileGroups.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-4">
          {tileGroups.map((g) => {
            const warn = g.items.some((t) => t.warn);
            return (
              <div key={g.label}>
                <div className={'flex items-center gap-2 mb-2 text-[11px] font-bold tracking-wider ' + (warn ? 'text-amber-600' : 'text-slate-400')}>
                  {warn && <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />}
                  {g.label}
                  <span className="font-semibold text-slate-300">{g.items.length}</span>
                </div>
                <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))' }}>
                  {g.items.map((t) => (
                    <button key={t.sec + t.label} onClick={() => onSelectService && onSelectService(t.key)} title={'Open ' + t.label}
                      className={'text-left rounded-xl border px-3 py-2.5 transition ' + (t.warn
                        ? 'bg-amber-50/70 border-amber-200 hover:border-amber-400 hover:bg-amber-50'
                        : 'bg-slate-50/60 border-slate-200 hover:border-brand-300 hover:bg-brand-50/40')}>
                      <div className={'text-xl font-bold leading-tight ' + (t.warn ? 'text-amber-700' : 'text-slate-800')}>{t.count}</div>
                      <div className="text-xs font-semibold text-slate-600 truncate">{t.label}</div>
                      {t.note && <div className="text-[11px] text-slate-400 truncate">{t.note}</div>}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
